import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil, Util} from 'yougile-util'
import {Hub} from 'yougile-model'
import {showConfirm} from '@components/common/base/notifications'
import {globals} from '@extensions/globals-base'
import {rightPanelStore} from './right-panel-store'

export const limitAnswers = 10
const defaultState = {
  question: '',
  answers: [{}, {}],
  multiSelectOn: false
}

interface Answer {
  text?: string
}

interface State {
  question: string
  answers: Answer[]
  multiSelectOn: boolean
}

export class VotingStore {
  state: State = Util.clone(defaultState)

  constructor() {
    makeAutoObservable(this, {state: observable.deep}, {autoBind: true})
  }

  get invalidCountAnswers(): boolean {
    return this.state.answers.length < 2
  }

  get leftCountAnswers(): number {
    return limitAnswers - this.state.answers.length
  }

  setQuestion(val: string): void {
    if (val?.length <= 250) {
      this.state.question = val
    }
  }

  maxAnswers(): boolean {
    return this.state.answers.length >= limitAnswers
  }

  addAnswer(): void {
    if (this.state.answers.length >= limitAnswers) {
      return
    }

    const answers = Util.clone(this.state.answers)
    answers.push({})
    this.state.answers = answers

    setTimeout(() => {
      const els = document.querySelectorAll<HTMLDivElement>(
        '.voting-content textarea'
      )
      if (els.length === 0) {
        return
      }
      const el = els.item(els.length - 1)
      el.focus()
    }, 300)
  }

  removeAnswer(idx: number): void {
    const answers = Util.clone(this.state.answers)
    answers.splice(idx, 1)
    this.state.answers = answers
  }

  setAnswer(idx: number, val: string): void {
    if (val?.length <= 70) {
      const answers = Util.clone(this.state.answers)

      answers[idx] ??= {}
      answers[idx].text = val

      this.state.answers = answers
    }
  }

  setMultiSelectOn(val: boolean): void {
    this.state.multiSelectOn = val
  }

  getAnswer(idx: number): string {
    return this.state.answers[idx].text || ''
  }

  canCreateVoting(): boolean {
    const hasQ = !!this.state.question
    if (!hasQ) {
      return false
    }

    return this.getAnswers().length >= 2
  }

  submit(task?: Hub): void {
    if (!this.canCreateVoting()) {
      return
    }

    rightPanelStore.closePopupVoting()

    if (!task) {
      return
    }

    const options = ArrUtil.filterUndefined(this.getAnswers().map(a => a.text))
    const {question, multiSelectOn} = votingStore.state

    task.chat.addMessage(globals.currentUserId, {
      text: question,
      textHtml: `<p style="font-weight: 600; font-size: 14px; line-height: 16px;">${question}</p>`,
      voting: {options, multiSelectOn}
    })
    task.commit()
    this.reset()
  }

  reset(): void {
    this.state = defaultState
  }

  cancel(): void {
    rightPanelStore.closePopupVoting()
    this.reset()
  }

  getAnswers(): Answer[] {
    return this.state.answers.filter(val => !!(val.text || '').trim())
  }

  async close(): Promise<void> {
    const hasQ = !!this.state.question
    const hasAnswers = this.getAnswers().length > 0

    if (hasQ || hasAnswers) {
      if (!(await showConfirm(Lang.msg.voting.confirmBack))) {
        return
      }
    }

    rightPanelStore.closePopupVoting()
    this.reset()
  }
}

export const votingStore = new VotingStore()
